import { LoaderCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AgentListingsSkeleton } from "@/components/agent/AgentPortalSkeletons";
import { ShortlistStudioPanel } from "@/components/agent-shortlist/ShortlistStudioPanel";
import { AgentTrustPanel } from "@/components/agent-trust/AgentTrustPanel";
import { FoundingMembersRibbon } from "@/components/FoundingMembersRibbon";
import { ListingCard } from "@/components/ListingCard";
import { MarketIntelligenceWidget } from "@/components/market-intelligence/MarketIntelligenceWidget";
import { SearchBar } from "@/components/SearchBar";
import { SellerLeadMagnet } from "@/components/SellerLeadMagnet";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { useSiteBranding } from "@/context/SiteContext";
import { useFastPolling } from "@/hooks/useFastPolling";
import { usePortalShortlist } from "@/hooks/usePortalShortlist";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { buildDistrictSeoCopy, formatCategoryLabel, isListingPriceOnRequest } from "@/lib/portalHelpers";
import { parseShortlistIds } from "@/lib/shortlistStorage";

const defaultOptions = { districts: [], tenures: [], property_types: [] };
const PAGE_SIZE = 24;

const buildPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 1) return [1];

  const items = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  const safeItems = [...items].filter((value) => value >= 1 && value <= totalPages).sort((left, right) => left - right);
  const output = [];

  safeItems.forEach((value, index) => {
    const previous = safeItems[index - 1];
    if (previous && value - previous > 1) output.push(`ellipsis-${previous}-${value}`);
    output.push(value);
  });

  return output;
};

export const ListingsPage = ({ defaultCategory, isDistrictRoute = false }) => {
  const navigate = useNavigate();
  const { branding, buildBrandedPath, isAgentPath, requestParams } = useSiteBranding();
  const advisorFirstName = String((branding?.name || "Advisor").split(" ")[0] || "Advisor");
  const shortlist = usePortalShortlist();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const shortlistParamValue = searchParams.get("shortlist") || "";
  const routeDistrict = isDistrictRoute ? params.district || "" : "";
  const [options, setOptions] = useState(defaultOptions);
  const [foundingRibbon, setFoundingRibbon] = useState({ active_count: 1, available_count: 17, members: [] });
  const [filters, setFilters] = useState({
    query: searchParams.get("q") || "",
    district: routeDistrict || searchParams.get("district") || "",
    tenure: searchParams.get("tenure") || "",
    property_type: searchParams.get("property_type") || "",
  });
  const [showPriceOnRequestOnly, setShowPriceOnRequestOnly] = useState(searchParams.get("price_mode") === "request");
  const [showStarBuysOnly, setShowStarBuysOnly] = useState(searchParams.get("star_buys") === "true");
  const [sortByValueGap, setSortByValueGap] = useState(searchParams.get("sort") === "value_gap_desc");
  const [showNewFoundingMembersOnly, setShowNewFoundingMembersOnly] = useState(searchParams.get("founding_members") === "new");
  const [showMultiAgentPropertiesOnly, setShowMultiAgentPropertiesOnly] = useState(searchParams.get("multi_agents") === "true");
  const [showUniqueListingsFlag, setShowUniqueListingsFlag] = useState(searchParams.get("unique_listings") === "true");
  const [pageCache, setPageCache] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalListingsCount, setTotalListingsCount] = useState(0);
  const [rawListingsCount, setRawListingsCount] = useState(0);
  const [multiAgentPropertyCount, setMultiAgentPropertyCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sharedShortlistLoading, setSharedShortlistLoading] = useState(false);
  const pageCacheRef = useRef({});
  const loadMoreRef = useRef(null);
  const listingsSectionRef = useRef(null);
  const sharedShortlistIds = useMemo(() => parseShortlistIds(shortlistParamValue), [shortlistParamValue]);

  const activeCategory = useMemo(() => (defaultCategory === "all" ? "" : defaultCategory), [defaultCategory]);
  const isRootMarketplace = useMemo(() => !requestParams?.agent_slug && !isDistrictRoute, [isDistrictRoute, requestParams]);
  const categoryBasePath = useMemo(() => {
    if (defaultCategory === "buy") return "/buy";
    if (defaultCategory === "rent") return "/rent";
    if (defaultCategory === "new_launch") return "/new-launches";
    return "/listings";
  }, [defaultCategory]);

  const listingsQuery = useMemo(
    () => ({
      query: filters.query || undefined,
      district: routeDistrict || filters.district || undefined,
      tenure: filters.tenure || undefined,
      property_type: filters.property_type || undefined,
      category: activeCategory || undefined,
      sort_by: sortByValueGap ? "value_gap_desc" : undefined,
      star_buys_only: showStarBuysOnly || undefined,
      price_on_request_only: activeCategory === "new_launch" && showPriceOnRequestOnly ? true : undefined,
      new_founding_members_only: isRootMarketplace && showNewFoundingMembersOnly ? true : undefined,
      multi_agent_properties_only: isRootMarketplace && showMultiAgentPropertiesOnly ? true : undefined,
    }),
    [activeCategory, filters.district, filters.property_type, filters.query, filters.tenure, isRootMarketplace, routeDistrict, showMultiAgentPropertiesOnly, showNewFoundingMembersOnly, showPriceOnRequestOnly, showStarBuysOnly, sortByValueGap],
  );
  const listingsQuerySignature = useMemo(() => JSON.stringify({ ...listingsQuery, ...requestParams }), [listingsQuery, requestParams]);

  const updatePaginationState = useCallback((pageNumber, payload) => {
    const nextItems = Array.isArray(payload?.items) ? payload.items : [];
    pageCacheRef.current = { ...pageCacheRef.current, [pageNumber]: nextItems };
    setPageCache(pageCacheRef.current);
    setTotalListingsCount(Number(payload?.total || 0));
    setRawListingsCount(Number(payload?.raw_total || 0));
    setMultiAgentPropertyCount(Number(payload?.multi_agent_property_total || 0));
    setTotalPages(Math.max(1, Number(payload?.total_pages || 1)));
  }, []);

  const fetchListingsPage = useCallback(
    async (pageNumber) => {
      const response = await api.get("/listings/public", {
        params: {
          ...listingsQuery,
          ...requestParams,
          page: pageNumber,
          page_size: PAGE_SIZE,
        },
      });
      return response.data || {};
    },
    [listingsQuery, requestParams],
  );

  const ensurePageLoaded = useCallback(
    async (targetPage, options = {}) => {
      const { showLoader = false } = options;
      if (showLoader) setLoading(true);
      else setLoadingMore(true);

      try {
        for (let pageNumber = 1; pageNumber <= targetPage; pageNumber += 1) {
          if (pageCacheRef.current[pageNumber]) continue;
          const payload = await fetchListingsPage(pageNumber);
          updatePaginationState(pageNumber, payload);
        }
        setCurrentPage(targetPage);
        return true;
      } catch {
        if (showLoader) {
          pageCacheRef.current = {};
          setPageCache({});
          setTotalListingsCount(0);
          setTotalPages(1);
          setCurrentPage(1);
        }
        return false;
      } finally {
        if (showLoader) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [fetchListingsPage, updatePaginationState],
  );

  const syncSearchParams = useCallback(() => {
    const nextSearch = {};
    if (filters.query) nextSearch.q = filters.query;
    if (!routeDistrict && filters.district) nextSearch.district = filters.district;
    if (filters.tenure) nextSearch.tenure = filters.tenure;
    if (filters.property_type) nextSearch.property_type = filters.property_type;
    if (showPriceOnRequestOnly) nextSearch.price_mode = "request";
    if (showStarBuysOnly) nextSearch.star_buys = "true";
    if (sortByValueGap) nextSearch.sort = "value_gap_desc";
    if (showNewFoundingMembersOnly) nextSearch.founding_members = "new";
    if (showMultiAgentPropertiesOnly) nextSearch.multi_agents = "true";
    if (showUniqueListingsFlag) nextSearch.unique_listings = "true";
    setSearchParams(nextSearch, { replace: true });
  }, [filters.district, filters.property_type, filters.query, filters.tenure, routeDistrict, setSearchParams, showMultiAgentPropertiesOnly, showNewFoundingMembersOnly, showPriceOnRequestOnly, showStarBuysOnly, showUniqueListingsFlag, sortByValueGap]);
  const seoCopy = isDistrictRoute
    ? buildDistrictSeoCopy(routeDistrict, filters.property_type || "Property")
    : {
        title: `${activeCategory ? `${formatCategoryLabel(activeCategory)} Listings` : "All Listings"} | SG Prop Portal`,
        description: "Search Singapore listings by district, tenure, and property type with live filtering powered by the Listings collection.",
      };

  useSeo(seoCopy);

  useEffect(() => {
    setFilters({
      query: searchParams.get("q") || "",
      district: routeDistrict || searchParams.get("district") || "",
      tenure: searchParams.get("tenure") || "",
      property_type: searchParams.get("property_type") || "",
    });
    setShowPriceOnRequestOnly(searchParams.get("price_mode") === "request");
    setShowStarBuysOnly(searchParams.get("star_buys") === "true");
    setSortByValueGap(searchParams.get("sort") === "value_gap_desc");
    setShowNewFoundingMembersOnly(searchParams.get("founding_members") === "new");
    setShowMultiAgentPropertiesOnly(searchParams.get("multi_agents") === "true");
    setShowUniqueListingsFlag(searchParams.get("unique_listings") === "true");
  }, [routeDistrict, searchParams]);

  useEffect(() => {
    let isActive = true;
    pageCacheRef.current = {};
    setPageCache({});
    setCurrentPage(1);
    setTotalListingsCount(0);
        setRawListingsCount(0);
        setMultiAgentPropertyCount(0);
    setTotalPages(1);

    const loadInitialPage = async () => {
      const didLoad = await ensurePageLoaded(1, { showLoader: true });
      if (isActive && didLoad) syncSearchParams();
    };

    loadInitialPage();

    return () => {
      isActive = false;
    };
  }, [ensurePageLoaded, listingsQuerySignature, syncSearchParams]);

  const listings = useMemo(
    () => Array.from({ length: currentPage }, (_, index) => pageCache[index + 1] || []).flat(),
    [currentPage, pageCache],
  );
  const hasMorePages = currentPage < totalPages;

  const loadMoreListings = useCallback(async () => {
    if (loading || loadingMore || !hasMorePages) return;
    await ensurePageLoaded(currentPage + 1);
  }, [currentPage, ensurePageLoaded, hasMorePages, loading, loadingMore]);

  const jumpToPage = useCallback(
    async (pageNumber) => {
      if (pageNumber < 1 || pageNumber > totalPages || loadingMore) return;
      if (pageNumber <= currentPage) {
        setCurrentPage(pageNumber);
      } else {
        await ensurePageLoaded(pageNumber);
      }
      listingsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [currentPage, ensurePageLoaded, loadingMore, totalPages],
  );

  const visibleListings = useMemo(
    () => (showPriceOnRequestOnly ? listings.filter((listing) => listing.category === "new_launch" && isListingPriceOnRequest(listing)) : listings),
    [listings, showPriceOnRequestOnly],
  );
  const underValuedListings = useMemo(() => visibleListings.filter((listing) => Number(listing.value_gap_percentage) > 5), [visibleListings]);
  const displayListings = useMemo(() => {
    const baseListings = showStarBuysOnly ? visibleListings.filter((listing) => Number(listing.value_gap_percentage) > 5) : visibleListings;
    if (!sortByValueGap) return baseListings;
    return [...baseListings].sort((left, right) => Number(right.value_gap_percentage || -999) - Number(left.value_gap_percentage || -999));
  }, [showStarBuysOnly, sortByValueGap, visibleListings]);
  const topStarBuys = useMemo(() => [...underValuedListings].sort((left, right) => Number(right.value_gap_percentage || 0) - Number(left.value_gap_percentage || 0)).slice(0, 3), [underValuedListings]);
  const agentListingsLastUpdatedAt = useMemo(() => [...displayListings].filter((listing) => listing.updated_at).sort((left, right) => new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime())[0]?.updated_at || null, [displayListings]);
  const searchInsights = useMemo(
    () => [
      { label: "Star Buys", value: underValuedListings.length },
      ...(topStarBuys[0] ? [{ label: "Best Value Gap", value: `+${Number(topStarBuys[0].value_gap_percentage || 0).toFixed(2)}%` }] : []),
    ],
    [topStarBuys, underValuedListings.length],
  );

  const applyFilters = useCallback(() => {
    const nextParams = new URLSearchParams();
    if (filters.query) nextParams.set("q", filters.query);
    if (filters.tenure) nextParams.set("tenure", filters.tenure);
    if (filters.property_type) nextParams.set("property_type", filters.property_type);
    if (showPriceOnRequestOnly) nextParams.set("price_mode", "request");
    if (showStarBuysOnly) nextParams.set("star_buys", "true");
    if (sortByValueGap) nextParams.set("sort", "value_gap_desc");
    if (showNewFoundingMembersOnly) nextParams.set("founding_members", "new");
    if (showMultiAgentPropertiesOnly) nextParams.set("multi_agents", "true");
    if (showUniqueListingsFlag) nextParams.set("unique_listings", "true");

    if (isDistrictRoute) {
      navigate(buildBrandedPath(`/district/${filters.district || routeDistrict}${nextParams.toString() ? `?${nextParams.toString()}` : ""}`));
      return;
    }

    if (filters.district) nextParams.set("district", filters.district);
    navigate(buildBrandedPath(`${categoryBasePath}${nextParams.toString() ? `?${nextParams.toString()}` : ""}`));
  }, [buildBrandedPath, categoryBasePath, filters, isDistrictRoute, navigate, routeDistrict, showMultiAgentPropertiesOnly, showNewFoundingMembersOnly, showPriceOnRequestOnly, showStarBuysOnly, sortByValueGap]);

  const resetFilters = useCallback(() => {
    setShowPriceOnRequestOnly(false);
    setShowStarBuysOnly(false);
    setSortByValueGap(false);
    setShowNewFoundingMembersOnly(false);
    setShowMultiAgentPropertiesOnly(false);
    setShowUniqueListingsFlag(false);
    if (isDistrictRoute) {
      navigate(buildBrandedPath(`/district/${routeDistrict}`));
      return;
    }
    setFilters({ query: "", district: "", tenure: "", property_type: "" });
    navigate(buildBrandedPath(categoryBasePath));
  }, [buildBrandedPath, categoryBasePath, isDistrictRoute, navigate, routeDistrict]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [optionsResponse, foundingRibbonResponse] = await Promise.all([
          api.get("/search-options"),
          isRootMarketplace ? api.get("/founding-members/ribbon") : Promise.resolve({ data: { active_count: 1, available_count: 17, members: [] } }),
        ]);
        setOptions(optionsResponse.data);
        setFoundingRibbon(foundingRibbonResponse.data);
      } catch {
        setOptions(defaultOptions);
        setFoundingRibbon({ active_count: 1, available_count: 17, members: [] });
      }
    };

    loadOptions();
  }, [isRootMarketplace]);

  useEffect(() => {
    if (!hasMorePages || loading || loadingMore) return undefined;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreListings();
        }
      },
      { rootMargin: "320px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMorePages, loadMoreListings, loading, loadingMore]);

  useFastPolling(() => {
    if (currentPage !== 1 || loadingMore) return;
    fetchListingsPage(1)
      .then((payload) => {
        updatePaginationState(1, payload);
      })
      .catch(() => undefined);
  }, 12000, true);

  useEffect(() => {
    if (!isAgentPath || !sharedShortlistIds.length) return undefined;
    let cancelled = false;
    const loadSharedShortlist = async () => {
      setSharedShortlistLoading(true);
      try {
        const response = await api.get("/listings/shortlist", {
          params: {
            ...requestParams,
            ids: sharedShortlistIds.join(","),
          },
        });
        if (cancelled) return;
        shortlist.mergeListings(response.data || []);
      } catch {
        if (!cancelled) toast.error("Unable to load the shared shortlist right now.");
      } finally {
        if (!cancelled) setSharedShortlistLoading(false);
      }
    };
    loadSharedShortlist();
    return () => {
      cancelled = true;
    };
  }, [isAgentPath, requestParams, sharedShortlistIds, shortlist.mergeListings]);

  const heading = isDistrictRoute
    ? (isAgentPath ? `Explore ${routeDistrict} with ${advisorFirstName}` : `Browse properties in ${routeDistrict}`)
    : isAgentPath
      ? activeCategory === "rent"
        ? `Rental homes with ${advisorFirstName}`
        : activeCategory === "buy"
          ? `Homes for sale with ${advisorFirstName}`
          : activeCategory === "new_launch"
            ? `New launches with ${advisorFirstName}`
            : `Homes curated by ${branding.name}`
      : activeCategory
        ? `${formatCategoryLabel(activeCategory)} listings`
        : "All Singapore listings";

  const supportingCopy = isDistrictRoute
    ? (isAgentPath
      ? `See what is currently available in ${routeDistrict}, shortlist favourites, and message ${advisorFirstName} directly when you want a second opinion or viewing slot.`
      : `This ${routeDistrict} landing page auto-pulls relevant listings from the database and gives owners two clear conversion paths: a free property valuation request and the branded 2026 Market Insights PDF.`)
    : isAgentPath
      ? `Filter by district, tenure, and property type, then shortlist the homes you like and WhatsApp ${advisorFirstName} when you are ready to move.`
      : "Filter the Listings collection by district, tenure, and property type. Results refresh instantly to keep the property discovery flow fast and focused.";
  const paginationItems = useMemo(() => buildPaginationItems(currentPage, totalPages), [currentPage, totalPages]);
  const loadedCount = displayListings.length;
  const countSummary = loading
    ? "Refreshing results..."
    : isAgentPath
      ? `Showing ${loadedCount} of ${totalListingsCount} homes${rawListingsCount ? ` • ${rawListingsCount} source records` : ""}`
      : `Showing ${loadedCount} of ${totalListingsCount} unique properties${rawListingsCount ? ` • ${rawListingsCount} raw records` : ""}`;
  const marketActivityLine = isAgentPath
    ? `${advisorFirstName} is currently curating ${totalListingsCount || 0} live homes across Singapore.`
    : `Monitoring thousands of active market signals across ${totalListingsCount || 0} exclusive unique opportunities.`;
  const marketIntelligenceDistrict = filters.district || routeDistrict || "";

  return (
    <div className={isAgentPath ? "agent-portal-page-stack" : ""}>
      <section className="section-shell">
        <div className={`grid gap-8 ${isDistrictRoute ? "lg:grid-cols-[1.05fr_0.95fr]" : ""}`}>
          <div className="max-w-3xl">
            <p className={`subtle-label ${isAgentPath ? "agent-portal-kicker" : ""}`}>Live Search Engine</p>
            <h1 className={`mt-3 text-4xl font-semibold text-[#001935] md:text-6xl ${isAgentPath ? "agent-portal-heading-lg" : ""}`.trim()} data-testid="listings-page-heading">
              {heading}
            </h1>
            <p className={`mt-4 text-base leading-8 text-[#31455f] ${isAgentPath ? "agent-portal-body-copy" : ""}`.trim()}>{supportingCopy}</p>

            {isDistrictRoute ? (
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <div className="rounded-3xl border border-[#001935]/10 bg-white/75 p-5 shadow-sm" data-testid="district-page-listing-count-card">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#5e6e82]">Active listings</p>
                  <p className="mt-2 text-3xl font-semibold text-[#001935]" data-testid="district-page-total-listings-value">{totalListingsCount}</p>
                </div>
                <div className="rounded-3xl border border-[#001935]/10 bg-white/75 p-5 shadow-sm" data-testid="district-page-value-hook-card">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#5e6e82]">Lead magnet</p>
                  <p className="mt-2 text-lg font-semibold text-[#001935]">2026 Market Insights PDF</p>
                </div>
                <div className="rounded-3xl border border-[#001935]/10 bg-white/75 p-5 shadow-sm" data-testid="district-page-cta-hook-card">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#5e6e82]">Seller CTA</p>
                  <p className="mt-2 text-lg font-semibold text-[#001935]">Free Valuation Request</p>
                </div>
              </div>
            ) : null}
          </div>

          {isDistrictRoute ? (
            <div className="luxury-panel p-6 md:p-8" data-testid="district-page-intro-card">
              <p className="subtle-label">District Landing Page</p>
              <h2 className="mt-3 text-3xl font-semibold text-white">Why {routeDistrict} sellers and buyers engage here</h2>
              <ul className="mt-5 grid gap-3 text-sm leading-7 text-slate-300">
                <li>• Auto-pulls the latest matching listings directly from the Listings collection.</li>
                <li>• Pairs district-specific discovery with valuation capture for owners.</li>
                <li>• Keeps brochure downloads, staging visuals, and financial tools one click away.</li>
              </ul>
            </div>
          ) : null}
        </div>

        <div className="mt-8">
          <SearchBar
            filters={filters}
            onApply={applyFilters}
            onFiltersChange={setFilters}
            onReset={resetFilters}
            options={options}
            insightPills={searchInsights}
            testIdPrefix="listings-search"
          />
          {isRootMarketplace && activeCategory !== "new_launch" ? (
            <div className="mt-5">
              <FoundingMembersRibbon activeCount={foundingRibbon.active_count} availableCount={foundingRibbon.available_count} members={foundingRibbon.members} />
            </div>
          ) : null}
        </div>
      </section>

      {isDistrictRoute && topStarBuys.length ? (
        <section className="section-shell pt-0">
          <div className="rounded-[30px] border border-[#D4AF37]/30 bg-[linear-gradient(135deg,#fff7e5_0%,#fffdf9_60%,#f6efe1_100%)] p-6 shadow-[0_18px_60px_rgba(212,175,55,0.08)] md:p-8" data-testid="district-page-star-buys-section">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-3xl">
                <p className="subtle-label">{routeDistrict} Star Buys</p>
                <h2 className="mt-3 text-3xl font-semibold text-[#001935]">Under-valued opportunities leading this district right now.</h2>
                <p className="mt-4 text-sm leading-8 text-[#42566f]">These listings are currently trading more than 5% below their active benchmark, making them the clearest value-led entries in {routeDistrict}.</p>
              </div>
              <div className="rounded-full border border-[#D4AF37]/35 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="district-page-star-buys-count-pill">
                {underValuedListings.length} star buys found
              </div>
            </div>
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              {topStarBuys.map((listing) => (
                <ListingCard key={`star-buy-${listing.id}`} listing={listing} />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="section-shell pt-4" ref={listingsSectionRef}>
        {isAgentPath && (shortlist.count || sharedShortlistIds.length) ? (
          <div className="mb-6">
            <ShortlistStudioPanel
              buildListingPath={(listingId) => buildBrandedPath(`/listings/${listingId}`)}
              count={shortlist.count}
              items={shortlist.items}
              onClearShortlist={() => {
                shortlist.clearAll();
                toast.success("Shortlist cleared");
              }}
              onCopyShareLink={async () => {
                await shortlist.copyShareLink();
                toast.success("Shortlist link copied");
              }}
              onExportCsv={() => {
                shortlist.exportCsv("agent-portal-shortlist");
                toast.success("Shortlist CSV exported");
              }}
              onNativeShare={async () => {
                await shortlist.shareNatively("Agent portal shortlist");
              }}
              onRemoveListing={(listingId) => {
                shortlist.removeListing(listingId);
                toast.success("Listing removed from shortlist");
              }}
              sharedViewActive={Boolean(sharedShortlistIds.length)}
              supportsNativeShare={shortlist.supportsNativeShare}
              testIdPrefix="listings-shortlist"
            />
            {sharedShortlistLoading ? <div className="mt-3 rounded-[20px] border border-[#D4AF37]/25 bg-[#fff8ea] px-4 py-3 text-sm text-[#8B6E3D]" data-testid="listings-shared-shortlist-loading">Loading shared shortlist...</div> : null}
          </div>
        ) : null}
        {isAgentPath ? (
          <div className="mb-6">
            <AgentTrustPanel
              branding={branding}
              buildBrandedPath={buildBrandedPath}
              compact
              lastUpdatedAt={agentListingsLastUpdatedAt}
              listingCount={displayListings.length}
              testIdPrefix="listings-agent-trust"
            />
          </div>
        ) : null}
        {!loading && activeCategory === "rent" && totalListingsCount === 0 ? (
          <div className="mb-6 rounded-[28px] border border-[#D4AF37]/26 bg-[linear-gradient(135deg,#fff9ef_0%,#f7efe2_56%,#efe2c8_100%)] p-6 shadow-[0_18px_50px_rgba(90,75,53,0.08)]" data-testid="rent-empty-feed-notice">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8B6E3D]">Rentals feed check</p>
            <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-3xl font-bold leading-none text-[#2C2C2C]" data-testid="rent-empty-feed-heading">
              No rental listings are currently loaded in the live feed.
            </h2>
            <p className="mt-4 max-w-3xl text-sm leading-8 text-[#5D584F]" data-testid="rent-empty-feed-copy">
              This route is now locked to rent-only results, so buy listings will not be mixed here. Right now, the database has no active rental inventory to display.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button className="outline-button" data-testid="rent-empty-feed-reset-button" onClick={resetFilters} type="button">
                Refresh rent filters
              </button>
              <button
                className="gold-button"
                data-testid="rent-empty-feed-contact-button"
                onClick={() => window.dispatchEvent(new CustomEvent("signature-advisor-start-viewing", { detail: { listing: { id: `rent-request-${branding?.subdomain_slug || 'main'}`, title: isAgentPath ? `Rental request with ${advisorFirstName}` : "Rental request", district: filters.district || routeDistrict || "Singapore", category: "rent", property_type: "Rental", status: "Requested" } } }))}
                type="button"
              >
                Request private viewing help
              </button>
            </div>
          </div>
        ) : null}
        {marketIntelligenceDistrict ? (
          <div className="mb-6" data-testid="listings-market-intelligence-shell">
            <MarketIntelligenceWidget
              autoLoad
              compact
              defaultDistrictCode={marketIntelligenceDistrict}
              subheading={isAgentPath ? `Give ${advisorFirstName}'s clients a quick district-level transaction snapshot before they browse the listings below.` : "Show a quick district-level caveat summary beside the active search results so buyers can compare price action before they shortlist."}
              testIdPrefix="listings-market-intelligence"
              title={`District ${marketIntelligenceDistrict.toUpperCase()} transaction snapshot`}
            />
          </div>
        ) : null}
        <div className="mb-4 rounded-full border border-[#001935]/10 bg-white/70 px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-[#42566f] shadow-sm" data-testid="market-activity-banner">
          {marketActivityLine}
        </div>
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-[#31455f]" data-testid="listings-results-count">
            {countSummary}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {rawListingsCount > totalListingsCount ? (
              <div className="rounded-full border border-[#D4AF37]/35 bg-[#fff4db] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="public-clean-vs-raw-count-pill">
                {rawListingsCount} raw / {totalListingsCount} unique
              </div>
            ) : null}
            {isRootMarketplace && multiAgentPropertyCount ? (
              <div className="rounded-full border border-sky-300/80 bg-sky-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-sky-700" data-testid="public-multi-agent-count-pill">
                {multiAgentPropertyCount} multi-agent properties
              </div>
            ) : null}
            {underValuedListings.length ? (
              <div className="rounded-full border border-emerald-300/80 bg-emerald-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700" data-testid="listings-star-buys-count-pill">
                {isAgentPath ? `${underValuedListings.length} standout homes` : `${underValuedListings.length} under-valued opportunities`}
              </div>
            ) : null}
            <button
              className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${showStarBuysOnly ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-[#001935]/12 bg-white text-[#42566f]"}`}
              data-testid="public-star-buys-only-toggle"
              onClick={() => setShowStarBuysOnly((current) => !current)}
              type="button"
            >
              {showStarBuysOnly ? "Showing: Star Buys Only" : "Star Buys Only"}
            </button>
            {isRootMarketplace ? (
              <button
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${showNewFoundingMembersOnly ? "border-[#D4AF37] bg-[#fff4db] text-[#8b6d16]" : "border-[#001935]/12 bg-white text-[#42566f]"}`}
                data-testid="public-new-founding-members-toggle"
                onClick={() => setShowNewFoundingMembersOnly((current) => !current)}
                type="button"
              >
                {showNewFoundingMembersOnly ? "Showing: New Founding Members" : "New Founding Members"}
              </button>
            ) : null}
            {isRootMarketplace ? (
              <button
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${showMultiAgentPropertiesOnly ? "border-sky-400 bg-sky-50 text-sky-700" : "border-[#001935]/12 bg-white text-[#42566f]"}`}
                data-testid="public-multi-agent-properties-toggle"
                onClick={() => setShowMultiAgentPropertiesOnly((current) => !current)}
                type="button"
              >
                <span>{showMultiAgentPropertiesOnly ? "Showing: Multi-Agent Properties" : "Show Multiple Agents Property"}</span>
                <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-bold ${showMultiAgentPropertiesOnly ? "bg-white text-sky-700" : "bg-sky-50 text-sky-700"}`} data-testid="public-multi-agent-properties-toggle-count">
                  {multiAgentPropertyCount}
                </span>
              </button>
            ) : null}
            <button
              className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${sortByValueGap ? "border-[#D4AF37] bg-[#fff4db] text-[#8b6d16]" : "border-[#001935]/12 bg-white text-[#42566f]"}`}
              data-testid="public-value-gap-sort-toggle"
              onClick={() => setSortByValueGap((current) => !current)}
              type="button"
            >
              {sortByValueGap ? "Value Gap High → Low" : "Sort by Value Gap"}
            </button>
            {activeCategory === "new_launch" ? (
              <button
                className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${showPriceOnRequestOnly ? "border-[#D4AF37] bg-[#fff4db] text-[#8b6d16]" : "border-[#001935]/12 bg-white text-[#42566f]"}`}
                data-testid="new-launch-price-on-request-filter-toggle"
                onClick={() => setShowPriceOnRequestOnly((current) => !current)}
                type="button"
              >
                {showPriceOnRequestOnly ? "Showing: Price available on request" : "Filter: Price available on request"}
              </button>
            ) : null}
          </div>
        </div>

        {loading ? (
          isAgentPath ? <AgentListingsSkeleton /> : <div className="luxury-panel flex min-h-64 items-center justify-center text-slate-300" data-testid="listings-loading-state">
            <LoaderCircle className="mr-3 h-5 w-5 animate-spin" /> Loading listings
          </div>
        ) : (
          <div className={`grid gap-5 md:gap-6 ${isAgentPath ? "md:grid-cols-2 xl:grid-cols-3" : "lg:grid-cols-3"}`.trim()} data-testid="listings-grid">
            {displayListings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}

        {!loading && displayListings.length > 0 ? (
          <div className="mt-8 space-y-4" data-testid="listings-pagination-summary">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[24px] border border-[#001935]/10 bg-white/80 px-5 py-4 text-sm text-[#42566f] shadow-[0_16px_50px_rgba(0,25,53,0.05)]">
              <p data-testid="listings-page-status">Page {currentPage} of {totalPages}</p>
              <p data-testid="listings-loaded-status">{loadedCount} loaded • {totalListingsCount} total</p>
            </div>

            {totalPages > 1 ? (
              <Pagination className="justify-center" data-testid="listings-pagination-nav">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      className={`rounded-full ${currentPage <= 1 ? "pointer-events-none opacity-50" : ""}`}
                      data-testid="listings-pagination-previous"
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        jumpToPage(currentPage - 1);
                      }}
                    />
                  </PaginationItem>
                  {paginationItems.map((item) => (
                    typeof item === "number" ? (
                      <PaginationItem key={item}>
                        <PaginationLink
                          className="rounded-full"
                          data-testid={`listings-pagination-page-${item}`}
                          href="#"
                          isActive={item === currentPage}
                          onClick={(event) => {
                            event.preventDefault();
                            jumpToPage(item);
                          }}
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={item}>
                        <PaginationEllipsis data-testid={`listings-pagination-${item}`} />
                      </PaginationItem>
                    )
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      className={`rounded-full ${currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}`}
                      data-testid="listings-pagination-next"
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        jumpToPage(currentPage + 1);
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            ) : null}

            {loadingMore ? (
              <div className="flex items-center justify-center gap-2 rounded-[24px] border border-[#D4AF37]/25 bg-[#fff8ea] px-5 py-4 text-sm font-medium text-[#8b6d16]" data-testid="listings-loading-more-state">
                <LoaderCircle className="h-4 w-4 animate-spin" /> Loading more listings
              </div>
            ) : null}

            <div className="h-4" data-testid="listings-infinite-scroll-sentinel" ref={loadMoreRef} />
          </div>
        ) : null}

        {!loading && displayListings.length === 0 ? (
          <div className="luxury-panel mt-6 p-8 text-sm text-slate-300" data-testid="listings-empty-state">
            No listings matched those filters yet. Reset the search or switch to another district to continue browsing.
          </div>
        ) : null}
      </section>

      {isDistrictRoute ? (
        <section className="section-shell pt-0">
          <SellerLeadMagnet
            contextLabel={`District landing page ${routeDistrict}`}
            description={`Owners in ${routeDistrict} can request a free valuation or unlock the branded 2026 Market Insights PDF while buyers continue browsing live district inventory.`}
            heading={`Turn ${routeDistrict} traffic into seller leads.`}
            testIdPrefix={`district-${routeDistrict.toLowerCase()}-seller-magnet`}
          />
        </section>
      ) : null}
    </div>
  );
};